<template>
  <div class="multi-autocomplete">
    <div class="input-container">
      <span>
        <label class="form-label mb-1" for="auto-complete">
          {{ label }}:
        </label>

        <small
          v-if="showInstructions"
          class="form-text float-end ms-2 mb-1 align-top"
        >
          Pressiona <kbd>enter</kbd> para agregar a la lista
        </small>
        <small
          v-if="showInvalid"
          class="form-text text-danger float-end mb-1 align-top"
        >
          {{ invalidValueMsg }}
        </small>
      </span>

      <input
        :class="inputClasses"
        :type="inputType"
        v-model="searchText"
        id="auto-complete"
        @keydown.enter.prevent="addItem"
        @keydown.down="navigateDown"
        @keydown.up="navigateUp"
        @focus="isFocused = true"
        @blur="handleBlur"
        :placeholder="placeholder"
        ref="input"
        :required="false"
        autocomplete="off"
      />

      <ul v-if="showDropdown" class="dropdown">
        <li
          v-for="(item, index) in filteredItems"
          :key="item.id"
          :class="getItemClass(index)"
          @click="selectItem(item)"
        >
          <small>
            {{ item.name }}
          </small>
        </li>
      </ul>
    </div>

    <div class="selected-items">
      <div v-for="item in value" :key="item.id" class="selected-item">
        <small>
          {{ item.name }}
        </small>
        <button @click="removeItem(item)" class="remove-btn">&times;</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "MultiAutocomplete",

  props: {
    value: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    allowCustom: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      default: "Type to search...",
    },
    inputType: {
      type: String,
      default: "text",
    },
    label: {
      type: String,
      default: "text",
    },
    invalidValueMsg: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      searchText: "",
      showDropdown: false,
      activeIndex: -1,
      isFocused: false,
      isValid: false,
      isSelecting: false,
    };
  },

  computed: {
    filteredItems() {
      if (!this.searchText) return [];

      return this.items.filter((item) => {
        const label = item.name.toLowerCase();
        const notSelected = !this.value.some(
          (selected) => selected.id === item.id
        );
        const search = this.searchText.toLowerCase();
        return label.includes(search) && notSelected;
      });
    },

    showInstructions() {
      return (
        (this.allowCustom && this.isFocused && this.isValid) ||
        this.activeIndex !== -1
      );
    },

    showInvalid() {
      return (
        this.allowCustom &&
        this.searchText &&
        this.isFocused &&
        !this.isValid &&
        this.activeIndex === -1
      );
    },

    inputClasses() {
      return {
        "is-invalid": !this.isValid && this.searchText,
      };
    },

    getItemClass() {
      return (index) => {
        return {
          active: index === this.activeIndex,
        };
      };
    },
  },

  methods: {
    selectItem(item) {
      this.isSelecting = true;
      this.$emit("add", item);

      this.resetInput();
      this.isSelecting = false;
    },

    removeItem(item) {
      this.$emit("remove", item);
    },

    addItem() {
      if (!this.isValid && this.activeIndex === -1) {
        return;
      }
      let newItem;

      if (this.activeIndex >= 0 && this.filteredItems[this.activeIndex]) {
        newItem = this.filteredItems[this.activeIndex];
      } else if (this.allowCustom && this.searchText.trim()) {
        newItem = {
          id: Date.now(),
          name: this.searchText.trim(),
        };
      }
      this.selectItem(newItem);
    },

    resetInput() {
      this.searchText = "";
      this.showDropdown = false;
      this.activeIndex = -1;
    },

    navigateDown() {
      if (this.activeIndex < this.filteredItems.length - 1) {
        this.activeIndex++;
      }
    },

    navigateUp() {
      if (this.activeIndex > -1) {
        this.activeIndex--;
      }
    },

    handleBlur() {
      setTimeout(() => {
        if (!this.isSelecting) {
          this.isFocused = false;
        }
      }, 150);
    },
  },

  watch: {
    searchText(newValue) {
      this.showDropdown = newValue.length > 0;
      this.isValid =
        this.searchText &&
        (!this.$refs.input.required || this.$refs.input.checkValidity());
    },

    isFocused(newValue) {
      if (!newValue) {
        this.resetInput();
      }
    },
  },
};
</script>

<style scoped>
.multi-autocomplete {
  width: 100%;
}

.input-container {
  position: relative;
}

input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
}

.dropdown li {
  padding: 8px;
  cursor: pointer;
}

.dropdown li.active {
  background-color: #e9ecef;
}

.dropdown li:hover {
  background-color: #f8f9fa;
}

.selected-items {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.selected-item {
  background-color: #e9ecef;
  border-radius: 3px;
  padding: 3px 6px;
  display: flex;
  align-items: center;
  gap: 3px;
}

.remove-btn {
  background: none;
  border: none;
  padding: 0 4px;
  cursor: pointer;
  font-size: 16px;
}
</style>
